import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { waLink } from "../config";
import { WhatsAppIcon } from "./icons";

export const AssistantPanel = ({ open, onClose }) => {
  const { t, lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ from: "bot", text: t.assistant.greeting }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // If language changes, refresh greeting when conversation is fresh
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 1 && prev[0].from === "bot") {
        return [{ from: "bot", text: t.assistant.greeting }];
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing]);

  const pushAnswer = (answerKey) => {
    setTyping(true);
    setTimeout(() => {
      setMessages((prev) => [...prev, { from: "bot", text: t.assistant.answers[answerKey] }]);
      setTyping(false);
    }, 650);
  };

  const handleOption = (opt) => {
    setMessages((prev) => [...prev, { from: "user", text: opt.label }]);
    pushAnswer(opt.id);
  };

  const handleFreeText = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setMessages((prev) => [...prev, { from: "user", text }]);
    setInput("");
    const lower = text.toLowerCase();
    const kw = t.assistant.keywords;
    const order = ["price", "hours", "location", "mechas", "progressiva", "treatments", "eval", "services"];
    let key = "fallback";
    for (const k of order) {
      if (kw[k] && kw[k].some((w) => lower.includes(w))) {
        key = k;
        break;
      }
    }
    pushAnswer(key);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.97 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-[5.5rem] right-4 sm:right-6 z-[80] flex h-[32rem] max-h-[70vh] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-gold/25 bg-white shadow-[0_35px_80px_-25px_rgba(142,75,92,0.5)]"
          data-testid="assistant-panel"
          role="dialog"
          aria-label={t.assistant.name}
        >
          <div className="flex items-center gap-3 bg-mauve px-4 py-3.5">
            <div className="relative">
              <img
                src="/assets/avatar-assistant.png"
                alt="Monique Santos"
                className="h-11 w-11 rounded-full border-2 border-cream/70 object-cover"
                data-testid="assistant-avatar"
              />
              <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-mauve bg-emerald-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-cream">{t.assistant.name}</p>
              <p className="text-[11px] text-cream/75">{t.assistant.status}</p>
            </div>
            <button onClick={onClose} aria-label="Fechar" data-testid="assistant-close" className="p-1.5 text-cream/80 hover:text-cream">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="assistant-scroll flex-1 space-y-3 overflow-y-auto bg-cream/60 px-4 py-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed shadow-sm ${
                    m.from === "user"
                      ? "rounded-br-md bg-rosedeep text-white"
                      : "rounded-bl-md border border-blush bg-white text-ink"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="flex gap-1 rounded-2xl rounded-bl-md border border-blush bg-white px-4 py-3">
                  {[0, 1, 2].map((d) => (
                    <motion.span
                      key={d}
                      className="h-1.5 w-1.5 rounded-full bg-rose"
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-blush bg-white px-3 pt-2.5">
            <div className="no-scrollbar flex gap-2 overflow-x-auto pb-2">
              {t.assistant.options.map((opt) => (
                <button
                  key={opt.id}
                  data-testid={`assistant-opt-${opt.id}`}
                  onClick={() => handleOption(opt)}
                  className="whitespace-nowrap rounded-full border border-rose/40 bg-blush/40 px-3.5 py-1.5 text-[11.5px] font-semibold text-rosedeep transition-colors hover:bg-rosedeep hover:text-white"
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <form onSubmit={handleFreeText} className="flex items-center gap-2 pb-1.5">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t.assistant.placeholder}
                data-testid="assistant-input"
                className="h-10 flex-1 rounded-full border border-blush bg-cream/70 px-4 text-[13px] outline-none focus:border-rose"
              />
              <button
                type="submit"
                aria-label="Enviar"
                data-testid="assistant-send"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-rosedeep text-white transition-colors hover:bg-ink"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
            <a
              href={waLink(t.waDefault)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="assistant-whatsapp"
              className="mb-2 flex items-center justify-center gap-2 rounded-full bg-rosedeep px-4 py-2.5 text-[12px] font-bold text-white transition-all duration-300 hover:bg-ink"
            >
              <WhatsAppIcon className="w-4 h-4" />
              {t.assistant.whatsapp}
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AssistantPanel;
